/**
 * Learning Paths Service
 * Handles visual learning path CRUD, A4 background/media uploads, public playback, and visit/click statistics.
 */
import api from './apiClient';
import { getAnonymousVisitorKey } from '../utils/anonymousVisitorKey';

const VISITOR_KEY_STORAGE = 'heatcrafthp.learningPathVisitorKey';

/**
 * Get or create a stable anonymous visitor key for public Learning Path analytics.
 * @returns {string} Anonymous visitor key
 */
export const getLearningPathVisitorKey = () => {
  const key = getAnonymousVisitorKey();

  try {
    if (!localStorage.getItem(VISITOR_KEY_STORAGE)) {
      localStorage.setItem(VISITOR_KEY_STORAGE, key);
    }
  } catch (error) {
    // Ignore localStorage write failures and still return the shared key.
  }

  return key;
};

/**
 * Get paginated learning paths visible to the current author.
 * @param {Object} params - Query params
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Paginated learning paths
 */
export const getLearningPaths = (params = {}, options = {}) => api.get('/learning-paths', params, options);

/**
 * Get one learning path for editing.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Learning path detail
 */
export const getLearningPath = (id, options = {}) => api.get(`/learning-paths/${id}`, {}, options);

/**
 * Get one published learning path for public playback.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Public learning path detail
 */
export const getPublicLearningPath = (id, options = {}) => api.get(`/learning-paths/${id}/public`, {}, options);

/**
 * Create a learning path.
 * @param {Object} data - Create payload
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Created path
 */
export const createLearningPath = (data, options = {}) => api.post('/learning-paths', data, options);

/**
 * Update a learning path.
 * @param {number} id - Learning path ID
 * @param {Object} data - Update payload
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Updated path detail
 */
export const updateLearningPath = (id, data, options = {}) => api.put(`/learning-paths/${id}`, data, options);

/**
 * Delete a learning path.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteLearningPath = (id, options = {}) => api.delete(`/learning-paths/${id}`, options);

/**
 * Publish a learning path.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const publishLearningPath = (id, options = {}) => api.post(`/learning-paths/${id}/publish`, {}, options);

/**
 * Unpublish a learning path.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const unpublishLearningPath = (id, options = {}) => api.post(`/learning-paths/${id}/unpublish`, {}, options);

/**
 * Upload or replace the A4 background image.
 * @param {number} id - Learning path ID
 * @param {File} file - Background image file
 * @param {Object} metadata - Optional metadata
 * @param {Object} options - Request options
 * @returns {Promise<Object>} File DTO
 */
export const uploadBackgroundImage = (id, file, metadata = {}, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (metadata.altText) formData.append('altText', metadata.altText);
  return api.upload(`/learning-paths/${id}/background-image`, formData, options);
};

/**
 * Delete the A4 background image.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteBackgroundImage = (id, options = {}) => api.delete(`/learning-paths/${id}/background-image`, options);

/**
 * Add a path element.
 * @param {number} id - Learning path ID
 * @param {Object} data - Element data
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Created element
 */
export const addPathElement = (id, data, options = {}) => api.post(`/learning-paths/${id}/elements`, data, options);

/**
 * Update a path element.
 * @param {number} elementId - Element ID
 * @param {Object} data - Element update data
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Updated element
 */
export const updatePathElement = (elementId, data, options = {}) => api.put(`/learning-paths/elements/${elementId}`, data, options);

/**
 * Delete a path element.
 * @param {number} elementId - Element ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deletePathElement = (elementId, options = {}) => api.delete(`/learning-paths/elements/${elementId}`, options);

/**
 * Reorder path elements.
 * @param {number} id - Learning path ID
 * @param {number[]} elementIds - Ordered element IDs
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const reorderPathElements = (id, elementIds, options = {}) => api.put(`/learning-paths/${id}/elements/reorder`, { elementIds }, options);

/**
 * Upload or replace path element media.
 * @param {number} elementId - Element ID
 * @param {'pdf'|'audio'|'video'} mediaType - Media type
 * @param {File} file - File to upload
 * @param {Object} metadata - Optional metadata
 * @param {Object} options - Request options
 * @returns {Promise<Object>} File DTO
 */
export const uploadElementMedia = (elementId, mediaType, file, metadata = {}, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (metadata.description) formData.append('description', metadata.description);
  return api.upload(`/learning-paths/elements/${elementId}/media/${mediaType}`, formData, options);
};

/**
 * Delete path element media.
 * @param {number} elementId - Element ID
 * @param {'pdf'|'audio'|'video'} mediaType - Media type
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteElementMedia = (elementId, mediaType, options = {}) => api.delete(`/learning-paths/elements/${elementId}/media/${mediaType}`, options);

/**
 * Record a public learning path visit.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const recordLearningPathVisit = (id, options = {}) => api.post(`/learning-paths/${id}/analytics/visit`, {
  anonymousVisitorKey: getLearningPathVisitorKey(),
}, options);

/**
 * Record a public path element interaction.
 * @param {number} pathId - Learning path ID
 * @param {number} elementId - Element ID
 * @param {string} interactionType - element-click, pdf-open, audio-open, video-open, link-open, quiz-open, survey-open, training-profile-open
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const recordLearningPathElementInteraction = (pathId, elementId, interactionType, options = {}) => api.post(
  `/learning-paths/${pathId}/elements/${elementId}/analytics/${interactionType}`,
  { anonymousVisitorKey: getLearningPathVisitorKey() },
  options
);

/**
 * Start or reuse a public Learning Path behavior analytics session.
 * @param {number} id - Learning path ID
 * @param {Object} data - Session metadata
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Session DTO
 */
export const startLearningPathSession = (id, data = {}, options = {}) => api.post(
  `/learning-paths/${id}/analytics/session/start`,
  {
    anonymousVisitorKey: getLearningPathVisitorKey(),
    ...data,
  },
  options
);

/**
 * Record a session-scoped Learning Path behavior event.
 * @param {number} pathId - Learning path ID
 * @param {number} sessionId - Session ID
 * @param {Object} data - Behavior event payload
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const recordLearningPathSessionEvent = (pathId, sessionId, data, options = {}) => api.post(
  `/learning-paths/${pathId}/analytics/session/${sessionId}/events`,
  {
    anonymousVisitorKey: getLearningPathVisitorKey(),
    ...data,
  },
  options
);

/**
 * End a public Learning Path behavior analytics session.
 * @param {number} pathId - Learning path ID
 * @param {number} sessionId - Session ID
 * @param {Object} data - Session end payload
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const endLearningPathSession = (pathId, sessionId, data = {}, options = {}) => api.post(
  `/learning-paths/${pathId}/analytics/session/${sessionId}/end`,
  {
    anonymousVisitorKey: getLearningPathVisitorKey(),
    ...data,
  },
  options
);

/**
 * Get learning path visit/click statistics.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Statistics DTO
 */
export const getLearningPathStatistics = (id, options = {}) => api.get(`/learning-paths/${id}/statistics`, {}, options);

/**
 * Get behavior analytics for a learning path.
 * @param {number} id - Learning path ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Behavior statistics DTO
 */
export const getLearningPathBehaviorStatistics = (id, options = {}) => api.get(`/learning-paths/${id}/statistics/behavior`, {}, options);

/**
 * Get template-driven funnel analytics for a learning path.
 * @param {number} id - Learning path ID
 * @param {string | null} templateKey - Funnel template key
 * @param {string | null} stageKeys - Optional custom ordered stage key list
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Funnel statistics DTO
 */
export const getLearningPathFunnelStatistics = (id, templateKey, stageKeys = null, options = {}) => api.get(
  `/learning-paths/${id}/statistics/funnels`,
  { templateKey, stageKeys },
  options,
);

/**
 * Get click statistics for a single path element.
 * @param {number} elementId - Element ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Element statistics DTO
 */
export const getLearningPathElementStatistics = (elementId, options = {}) => api.get(`/learning-paths/elements/${elementId}/statistics`, {}, options);

const learningPathsService = {
  getLearningPathVisitorKey,
  getLearningPaths,
  getLearningPath,
  getPublicLearningPath,
  createLearningPath,
  updateLearningPath,
  deleteLearningPath,
  publishLearningPath,
  unpublishLearningPath,
  uploadBackgroundImage,
  deleteBackgroundImage,
  addPathElement,
  updatePathElement,
  deletePathElement,
  reorderPathElements,
  uploadElementMedia,
  deleteElementMedia,
  recordLearningPathVisit,
  recordLearningPathElementInteraction,
  startLearningPathSession,
  recordLearningPathSessionEvent,
  endLearningPathSession,
  getLearningPathStatistics,
  getLearningPathBehaviorStatistics,
  getLearningPathFunnelStatistics,
  getLearningPathElementStatistics,
};

export default learningPathsService;
